import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useDocumentTitle from "../../hooks/useDocumentTitle";
import useModalEscape from "../../hooks/useModalEscape";
import Sidebar from '../../components/Sidebar/Sidebar';
import './UserManagement.css';

import {
  fetchRegions,
  fetchProvincesByRegion,
  fetchCitiesMunicipalitiesByProvince,
  fetchBarangaysByCityMunicipality,
} from '../../api/psgc';
import {
  provisionUser,
  validateLocation,
  validateStep1,
  validatePasswordsMatch,
} from '../../services/provisionUser';
import { notifyError, notifySuccess } from '../../notify';
import { listUsers } from '../../services/listUsers';
import { deleteUser } from '../../services/deleteUser';
import { updateUserProfile } from '../../services/updateUserProfile';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';

// lucide-static icons
import {
  Activity,
  Users,
  Settings,
  Search,
  Plus,
  Mail,
  User,
  Lock,
  Eye,
  EyeOff,
  MoreVertical,
  Edit,
  Trash2,
  AlertTriangle,
  Filter,
  Check,
  ChevronDown,
  X
} from 'lucide-static';
const avisaLogo = `${process.env.PUBLIC_URL}/images/avisa.svg`;

// Icon wrapper
const Icon = ({ icon, className, size = 20, color, ...props }) => {
  const getProcessedSvg = () => {
    let svgString = icon;
    if (size) {
      svgString = svgString.replace('<svg', `<svg width="${size}" height="${size}"`);
    }
    if (color) {
      svgString = svgString.replace('<svg', `<svg color="${color}" stroke="${color}"`);
    }
    if (className) {
      svgString = svgString.replace('<svg', `<svg class="${className}"`);
    }
    return svgString; 
  };
  return <span dangerouslySetInnerHTML={{ __html: getProcessedSvg() }} {...props} />;
};

const UserManagement = () => {
  useDocumentTitle("Avisa Admin Dashboard");

  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const USERS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const { users, usersLoaded, refreshUsers } = useData();
  const [usersLoading, setUsersLoading] = useState(!usersLoaded);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);

  // Edit User Modal state
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);
  const [editModalError, setEditModalError] = useState('');
  const [editFormData, setEditFormData] = useState({
    name: '',
    username: '',
    password: '',
    confirmPassword: '',
  });

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    role: "all",
    displayMode: "name",
    sortBy: "newest",
  });
  const [tempFilters, setTempFilters] = useState(filters);

  const normalizeRole = (role) => {
    const value = String(role || '').trim().toLowerCase();
    if (value === 'sysadmin' || value === 'system_admin' || value === 'system admin') return 'sysadmin';
    if (value === 'admin') return 'admin';
    return 'user';
  };

  const formatRoleLabel = (role) => {
    const normalized = normalizeRole(role);
    if (normalized === 'sysadmin') return 'System Administrator';
    if (normalized === 'admin') return 'Administrator';
    return 'User';
  };

  const getUserAvatarSrc = (user) =>
    user?.profilePhoto || user?.profile?.profilePhoto || user?.photoURL || user?.photoUrl || null;

  const timeAgo = (ms) => {
    if (!ms || typeof ms !== 'number') return '—';
    const diff = Date.now() - ms;
    if (diff < 60 * 1000) return 'just now';
    const mins = Math.floor(diff / (60 * 1000));
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(diff / (60 * 60 * 1000));
    if (hours < 24) return `${hours} hours ago`;
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    if (days < 30) return `${days} days ago`;
    return new Date(ms).toLocaleDateString();
  };

  const loadUsersData = async () => {
    setUsersLoading(true);
    await refreshUsers();
    setUsersLoading(false);
  };

  useEffect(() => {
    if (!usersLoaded) {
      loadUsersData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersLoaded]);

  const closeAllModals = () => {
    closeModal();
    closeEditModal();
    closeDeleteModal();
    setIsFilterModalOpen(false);
  };

  // Add User (multi-step) modal state
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [addUserStep, setAddUserStep] = useState(1);
  const [isAddUserSubmitting, setIsAddUserSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useModalEscape(closeAllModals, isAddUserModalOpen || isEditUserModalOpen || isDeleteModalOpen || isFilterModalOpen);

  const [basicUserInfo, setBasicUserInfo] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user',
  });

  const [adminAccessLevel, setAdminAccessLevel] = useState('');

  const [psgc, setPsgc] = useState({
    regions: [],
    provinces: [],
    municipalities: [],
    barangays: [],
  });
  const [psgcLoading, setPsgcLoading] = useState({
    regions: false,
    provinces: false,
    municipalities: false,
    barangays: false,
  });
  const [psgcSelection, setPsgcSelection] = useState({
    regionCode: '',
    provinceCode: '',
    municipalityCode: '',
    barangayCode: '',
  });

  // Determine active nav item based on current URL


  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleBasicInfoChange = (e) => {
    const { name, value } = e.target;
    setBasicUserInfo((prev) => ({ ...prev, [name]: value }));
    if (modalError) setModalError('');
  };

  const openAddUserModal = () => {
    setIsAddUserModalOpen(true);
    setAddUserStep(1);
    setIsAddUserSubmitting(false);
    setModalError('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setBasicUserInfo({ name: '', username: '', email: '', password: '', confirmPassword: '', role: 'user' });
    setPsgc({ regions: [], provinces: [], municipalities: [], barangays: [] });
    setPsgcLoading({ regions: false, provinces: false, municipalities: false, barangays: false });
    setPsgcSelection({ regionCode: '', provinceCode: '', municipalityCode: '', barangayCode: '' });
    setAdminAccessLevel('none');
  };

  const loadRegionsIfNeeded = async () => {
    if (psgc.regions.length > 0) return;
    setPsgcLoading((p) => ({ ...p, regions: true }));
    try {
      const regions = await fetchRegions();
      setPsgc((p) => ({ ...p, regions }));
    } catch {
      notifyError('Failed to load regions.');
    } finally {
      setPsgcLoading((p) => ({ ...p, regions: false }));
    }
  };

  React.useEffect(() => {
    if (isAddUserModalOpen) {
      loadRegionsIfNeeded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddUserModalOpen]);

  React.useEffect(() => {
    const regionCode = psgcSelection.regionCode;
    if (!isAddUserModalOpen || addUserStep !== 2) return;
    if (!regionCode) return;

    setPsgcLoading((p) => ({ ...p, provinces: true }));
    fetchProvincesByRegion(regionCode)
      .then((provinces) => setPsgc((p) => ({ ...p, provinces })))
      .catch(() => notifyError('Failed to load provinces.'))
      .finally(() => setPsgcLoading((p) => ({ ...p, provinces: false })));
  }, [psgcSelection.regionCode, isAddUserModalOpen, addUserStep]);

  React.useEffect(() => {
    const provinceCode = psgcSelection.provinceCode;
    if (!isAddUserModalOpen || addUserStep !== 2) return;
    if (!provinceCode) return;

    setPsgcLoading((p) => ({ ...p, municipalities: true }));
    fetchCitiesMunicipalitiesByProvince(provinceCode)
      .then((municipalities) => setPsgc((p) => ({ ...p, municipalities })))
      .catch(() => notifyError('Failed to load municipalities.'))
      .finally(() => setPsgcLoading((p) => ({ ...p, municipalities: false })));
  }, [psgcSelection.provinceCode, isAddUserModalOpen, addUserStep]);

  React.useEffect(() => {
    const municipalityCode = psgcSelection.municipalityCode;
    if (!isAddUserModalOpen || addUserStep !== 2) return;
    if (!municipalityCode) return;

    setPsgcLoading((p) => ({ ...p, barangays: true }));
    fetchBarangaysByCityMunicipality(municipalityCode)
      .then((barangays) => setPsgc((p) => ({ ...p, barangays })))
      .catch(() => notifyError('Failed to load barangays.'))
      .finally(() => setPsgcLoading((p) => ({ ...p, barangays: false })));
  }, [psgcSelection.municipalityCode, isAddUserModalOpen, addUserStep]);

  const handleNextFromStep1 = async () => {
    const check = validateStep1(basicUserInfo);
    if (!check.ok) {
      notifyError(check.message);
      setModalError('');
      return;
    }

    const pw = validatePasswordsMatch(basicUserInfo);
    if (!pw.ok) {
      notifyError(pw.message);
      setModalError('');
      return;
    }

    if (basicUserInfo.role === 'admin' && (!adminAccessLevel || adminAccessLevel === 'none' || adminAccessLevel === '')) {
      notifyError('Please select Admin Access (Provincial, Municipal or Barangay).');
      setModalError('');
      return;
    }

    setBasicUserInfo((prev) => ({ ...prev, ...check.normalized }));
    setModalError('');
    setAddUserStep(2);
    await loadRegionsIfNeeded();
  };

  const handleFinalAddUser = async (e) => {
    e.preventDefault();
    if (isAddUserSubmitting) return;

    const regionName = psgc.regions.find((r) => r.code === psgcSelection.regionCode)?.name || '';
    const provinceName = psgc.provinces.find((p) => p.code === psgcSelection.provinceCode)?.name || '';
    const municipalityName = psgc.municipalities.find((m) => m.code === psgcSelection.municipalityCode)?.name || '';
    const barangayName = psgc.barangays.find((b) => b.code === psgcSelection.barangayCode)?.name || '';

    const locationCheck = validateLocation({
      region: regionName,
      province: provinceName,
      municipality: municipalityName,
      barangay: barangayName,
    });

    if (!locationCheck.ok) {
      notifyError(locationCheck.message);
      setModalError('');
      return;
    }

    setIsAddUserSubmitting(true);
    setModalError('');
    try {
      // If creating an admin, ensure an access level was chosen
      if (basicUserInfo.role === 'admin' && (!adminAccessLevel || adminAccessLevel === '')) {
        notifyError('Please select an admin access level (Provincial, Municipal or Barangay).');
        setIsAddUserSubmitting(false);
        return;
      }
      const result = await provisionUser({
        name: basicUserInfo.name,
        username: basicUserInfo.username,
        email: basicUserInfo.email,
        password: basicUserInfo.password,
        role: basicUserInfo.role || 'user',
        adminaccessLevel: basicUserInfo.role === 'admin' ? adminAccessLevel : 'none',
        location: locationCheck.normalized,
      });
      notifySuccess(result?.sentEmail ? 'User created. Password reset email sent.' : 'User created.');
      await refreshUsers();
      closeModal();
    } catch (err) {
      notifyError(err?.message || 'Failed to create user.');
    } finally {
      setIsAddUserSubmitting(false);
    }
  };

  // Close modal and reset form
  const closeModal = () => {
    setIsAddUserModalOpen(false);
    setAddUserStep(1);
    setIsAddUserSubmitting(false);
    setModalError('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setBasicUserInfo({ name: '', username: '', email: '', password: '', confirmPassword: '', role: 'user' });
    setPsgc({ regions: [], provinces: [], municipalities: [], barangays: [] });
    setPsgcLoading({ regions: false, provinces: false, municipalities: false, barangays: false });
    setPsgcSelection({ regionCode: '', provinceCode: '', municipalityCode: '', barangayCode: '' });
    setAdminAccessLevel('');
  };

  // Edit Modal Handlers
  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name || '',
      username: user.username || '',
      password: '',
      confirmPassword: '',
    });
    setIsEditUserModalOpen(true);
    setEditModalError('');
  };

  const closeEditModal = () => {
    setIsEditUserModalOpen(false);
    setSelectedUser(null);
    setEditFormData({ name: '', username: '', password: '', confirmPassword: '' });
    setEditModalError('');
    setShowEditPassword(false);
    setShowEditConfirmPassword(false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    if (editModalError) setEditModalError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (isEditSubmitting || !selectedUser?.uid) return;

    if (editFormData.password !== editFormData.confirmPassword) {
      notifyError('Passwords do not match!');
      setEditModalError('');
      return;
    }

    if (editFormData.password && editFormData.password.length < 6) {
      notifyError('Password must be at least 6 characters');
      setEditModalError('');
      return;
    }

    setIsEditSubmitting(true);
    try {
      await updateUserProfile({
        uid: selectedUser.uid,
        name: editFormData.name,
        username: editFormData.username,
        password: editFormData.password || undefined,
      });
      notifySuccess('User account updated.');
      await refreshUsers();
      closeEditModal();
    } catch (err) {
      notifyError(err?.message || 'Failed to update user account.');
      setEditModalError('');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleRemoveUser = (targetUser) => {
    if (!targetUser?.uid) {
      notifyError('Missing user id.');
      return;
    }
    setUserToDelete(targetUser);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
    setIsDeleteSubmitting(false);
  };

  const confirmRemoveUser = async () => {
    if (isDeleteSubmitting || !userToDelete?.uid) return;

    setIsDeleteSubmitting(true);
    try {
      await deleteUser({ uid: userToDelete.uid });
      notifySuccess('User account removed.');
      await refreshUsers();
      closeDeleteModal();
    } catch (err) {
      notifyError(err?.message || 'Failed to remove user account.');
    } finally {
      setIsDeleteSubmitting(false);
    }
  };

  const filteredUsers = useMemo(() => {
    let result = [...users];

    // Search term filter - search name, username, and email
    const q = String(searchTerm || "").trim().toLowerCase();
    if (q) {
      result = result.filter((u) => {
        const name = String(u.name || "").toLowerCase();
        const username = String(u.username || "").toLowerCase();
        const email = String(u.email || "").toLowerCase();
        return (
          name.includes(q) || username.includes(q) || email.includes(q)
        );
      });
    }

    // Role filter
    if (filters.role !== "all") {
      result = result.filter((u) => {
        const userRole = normalizeRole(u.role);
        const targetRole = filters.role.toLowerCase();

        if (targetRole === "system admin" || targetRole === "system_admin") {
          return userRole === "sysadmin";
        }
        return userRole === targetRole;
      });
    }

    // Admin access level filtering - admins can only see users within their jurisdiction
    // System admins are never visible to regular admins
    const currentUserRole = normalizeRole(currentUser?.role);
    const currentUserAccessLevel = String(currentUser?.adminaccessLevel || '').toLowerCase();
    const currentUserLocation = currentUser?.location || {};

    if (currentUserRole === 'admin') {
      // Filter out system admins (they're never visible to regular admins)
      result = result.filter((u) => normalizeRole(u.role) !== 'sysadmin');

      // Apply location-based filtering based on admin access level
      if (currentUserAccessLevel === 'provincial') {
        // Provincial admins see all users in their province
        const adminProvince = String(currentUserLocation.province || '').toLowerCase();
        if (adminProvince) {
          result = result.filter((u) => {
            const userProvince = String(u.location?.province || '').toLowerCase();
            return userProvince === adminProvince;
          });
        }
      } else if (currentUserAccessLevel === 'municipal') {
        // Municipal admins see all users in their municipality
        const adminMunicipality = String(currentUserLocation.municipality || '').toLowerCase();
        if (adminMunicipality) {
          result = result.filter((u) => {
            const userMunicipality = String(u.location?.municipality || '').toLowerCase();
            return userMunicipality === adminMunicipality;
          });
        }
      } else if (currentUserAccessLevel === 'barangay') {
        // Barangay admins see all users in their barangay
        const adminBarangay = String(currentUserLocation.barangay || '').toLowerCase();
        const adminMunicipality = String(currentUserLocation.municipality || '').toLowerCase();
        if (adminBarangay && adminMunicipality) {
          result = result.filter((u) => {
            const userBarangay = String(u.location?.barangay || '').toLowerCase();
            const userMunicipality = String(u.location?.municipality || '').toLowerCase();
            return userBarangay === adminBarangay && userMunicipality === adminMunicipality;
          });
        }
      }
    }

    // Sorting
    if (filters.sortBy === "newest") {
      result.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));
    } else if (filters.sortBy === "oldest") {
      result.sort((a, b) => (a.createdAtMs || 0) - (b.createdAtMs || 0));
    } else if (filters.sortBy === "alphabetical") {
      result.sort((a, b) => {
        const nameA = String(a.name || a.username || "").toLowerCase();
        const nameB = String(b.name || b.username || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    return result;
  }, [users, searchTerm, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * USERS_PER_PAGE;
    return filteredUsers.slice(start, start + USERS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, users]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="dashboard-container">
      <Sidebar />

      <main className="main-content">
        {/* Top Bar */}
        <div className="user-management-top-bar">
          <div className="user-search-section">
            <div className="user-search-wrapper">
              <Icon icon={Search} size={18} className="user-search-icon" />
              <input
                type="text"
                placeholder="Search Users..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchTerm(searchInput);
                  }
                }}
                className="user-search-input"
              />
              <button
                className={`user-filter-toggle-btn ${isFilterModalOpen ? "active" : ""}`}
                onClick={() => {
                  if (!isFilterModalOpen) {
                    setTempFilters(filters);
                  }
                  setIsFilterModalOpen(!isFilterModalOpen);
                }}
                title="Filter & Sort"
              >
                <Icon icon={Filter} size={18} />
              </button>

              {isFilterModalOpen && (
                <>
                  <div
                    className="user-filter-modal-overlay"
                    onClick={() => setIsFilterModalOpen(false)}
                  />
                  <div className="user-filter-modal-content">
                    <div className="user-filter-modal-header">
                      <span className="user-filter-modal-title">Filter & Sort</span>
                      <button
                        className="user-filter-modal-close-icon"
                        onClick={() => setIsFilterModalOpen(false)}
                      >
                        <Icon icon={X} size={16} />
                      </button>
                    </div>

                    <div className="user-filter-modal-body">
                      <div className="user-filter-section">
                        <label className="user-filter-section-label">Role Filter</label>
                        <div className="user-filter-chips">
                          {["all", "system admin", "admin", "user"].map((r) => (
                            <button
                              key={r}
                              className={`user-filter-chip ${tempFilters.role === r ? "active" : ""}`}
                              onClick={() => setTempFilters({ ...tempFilters, role: r })}
                            >
                              {r.charAt(0).toUpperCase() + r.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="user-filter-section">
                        <label className="user-filter-section-label">Display Toggle</label>
                        <div className="user-filter-toggle-group">
                          <button
                            className={`user-filter-toggle-item ${tempFilters.displayMode === "name" ? "active" : ""}`}
                            onClick={() => setTempFilters({ ...tempFilters, displayMode: "name" })}
                          >
                            <span>Show Name</span>
                            {tempFilters.displayMode === "name" && (
                              <Icon icon={Check} size={14} className="check-icon" />
                            )}
                          </button>
                          <button
                            className={`user-filter-toggle-item ${tempFilters.displayMode === "username" ? "active" : ""}`}
                            onClick={() => setTempFilters({ ...tempFilters, displayMode: "username" })}
                          >
                            <span>Show Username</span>
                            {tempFilters.displayMode === "username" && (
                              <Icon icon={Check} size={14} className="check-icon" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="user-filter-section">
                        <label className="user-filter-section-label">Sort Options</label>
                        <div className="user-filter-sort-options">
                          {[
                            { id: "newest", label: "Newest → Oldest" },
                            { id: "oldest", label: "Oldest → Newest" },
                            { id: "alphabetical", label: "Alphabetical (A–Z)" },
                          ].map((s) => (
                            <button
                              key={s.id}
                              className={`user-filter-sort-item ${tempFilters.sortBy === s.id ? "active" : ""}`}
                              onClick={() => setTempFilters({ ...tempFilters, sortBy: s.id })}
                            >
                              {s.label}
                              {tempFilters.sortBy === s.id && (
                                <Icon icon={Check} size={14} className="check-icon" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="user-filter-modal-footer">
                      <button
                        className="user-filter-reset-btn"
                        onClick={() =>
                          setTempFilters({
                            role: "all",
                            displayMode: "name",
                            sortBy: "newest",
                          })
                        }
                      >
                        Reset
                      </button>
                      <button
                        className="user-filter-apply-btn"
                        onClick={() => {
                          setFilters(tempFilters);
                          setIsFilterModalOpen(false);
                        }}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <button 
            className="add-user-btn"
            onClick={openAddUserModal}
          >
            Add User
          </button>
        </div>

        {/* Main container */}
        <div className="user-management-container">
          <div className="user-management-header">
            <h1 className="user-management-title">User Management</h1>
          </div>

          <p className="user-management-subtitle">
            Manage user accounts, permissions, and access
          </p>

          <div className="users-items-container">
            {usersLoading ? (
              <div className="no-users-found">
                <Icon icon={Users} size={48} className="no-users-icon" />
                <span>Loading users...</span>
              </div>
            ) : filteredUsers.length > 0 ? (
              paginatedUsers.map((user, index) => (
                <div key={user.uid || index} className="user-item">
                  <div className="user-item-left">
                    <div className="user-avatar-item">
                      {getUserAvatarSrc(user) ? (
                        <img
                          src={getUserAvatarSrc(user)}
                          alt=""
                          className="user-avatar-image"
                        />
                      ) : (
                        (user.name || user.username || '?').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="user-info-item">
                      <span className="user-name-item">
                        {user.username && user.name
                          ? `${user.username} - ${user.name}`
                          : user.username || user.name || "—"}
                      </span>
                      <span className="user-email-item">{user.email || '—'}</span>
                    </div>
                  </div>
                  <div className="user-item-right">
                    <span className="user-role-badge">{formatRoleLabel(user.role)}</span>
                    <span className="user-time-item">
                      {timeAgo(user.lastActivityMs || user.lastLoginMs || user.lastSeenAtMs || user.createdAtMs)}
                    </span>
                    <button
                      type="button"
                      className="user-action-btn"
                      onClick={() => openEditModal(user)}
                      title={normalizeRole(user.role) === 'user' ? "Manage Account" : "Access Restricted"}
                      disabled={normalizeRole(user.role) !== 'user'}
                    >
                      <Icon icon={MoreVertical} size={18} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-users-found">
                <Icon icon={Users} size={48} className="no-users-icon" />
                <span>No users found</span>
              </div>
            )}
          </div>

          {!usersLoading && filteredUsers.length > 0 && (
            <div className="users-pagination">
              <span className="users-pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <div className="users-pagination-actions">
                <button
                  type="button"
                  className="users-pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="users-pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Add User Modal - With Icons Inside Text Fields */}
      {isAddUserModalOpen && (
        <div className="add-user-modal-overlay" onClick={closeModal}>
          <div className="add-user-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="add-user-modal-close" onClick={closeModal}>
              ✕
            </button>

            {modalError && <div className="add-user-error-message">{modalError}</div>}

            {addUserStep === 1 ? (
              <div>
                <div className="add-user-modal-body">
                  <div className="add-user-form-field">
                        <label className="add-user-field-label">Name</label>
                    <div className="add-user-input-wrapper">
                      <Icon icon={User} size={18} className="add-user-input-icon" />
                      <input
                        type="text"
                        name="name"
                        value={basicUserInfo.name}
                        onChange={handleBasicInfoChange}
                        placeholder="Juan Dela Cruz"
                        className="add-user-field-input-with-icon"
                        required
                      />
                    </div>
                  </div>

                      <div className="add-user-form-field">
                        <label className="add-user-field-label">Role</label>
                        <div style={{ position: 'relative' }}>
                          <select
                            name="role"
                            value={basicUserInfo.role}
                            onChange={(e) => {
                              const val = e.target.value;
                              setBasicUserInfo((prev) => ({ ...prev, role: val }));
                              // reset admin access level if role changed
                              if (val !== 'admin') setAdminAccessLevel('none');
                              if (modalError) setModalError('');
                            }}
                            className="add-user-field-select"
                          >
                            <option value="user">User</option>
                            <option value="admin">Administrator</option>
                            <option value="sysadmin">System Administrator</option>
                          </select>
                          <span className="select-chevron">
                            <Icon icon={ChevronDown} size={16} />
                          </span>
                        </div>
                      </div>

                      {basicUserInfo.role === 'admin' && (
                        <div className="add-user-form-field">
                          <label className="add-user-field-label">Access</label>
                          <div style={{ position: 'relative' }}>
                            <select
                              value={adminAccessLevel}
                              onChange={(e) => {
                                setAdminAccessLevel(e.target.value);
                                if (modalError) setModalError('');
                              }}
                              className="add-user-field-select"
                              required
                            >
                              <option value="">Select Access Level</option>
                              <option value="provincial">Provincial</option>
                              <option value="municipal">Municipal</option>
                              <option value="barangay">Barangay</option>
                            </select>
                            <span className="select-chevron">
                              <Icon icon={ChevronDown} size={16} />
                            </span>
                          </div>
                        </div>
                      )}

                  <div className="add-user-form-field">
                    <label className="add-user-field-label">Username</label>
                    <div className="add-user-input-wrapper">
                      <Icon icon={User} size={18} className="add-user-input-icon" />
                      <input
                        type="text"
                        name="username"
                        value={basicUserInfo.username}
                        onChange={handleBasicInfoChange}
                        placeholder="juan_dela_cruz"
                        className="add-user-field-input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="add-user-form-field">
                    <label className="add-user-field-label">Email</label>
                    <div className="add-user-input-wrapper">
                      <Icon icon={Mail} size={18} className="add-user-input-icon" />
                      <input
                        type="email"
                        name="email"
                        value={basicUserInfo.email}
                        onChange={handleBasicInfoChange}
                        placeholder="hello@example.com"
                        className="add-user-field-input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="add-user-form-field">
                    <label className="add-user-field-label">Password</label>
                    <div className="add-user-input-wrapper">
                      <Icon icon={Lock} size={18} className="add-user-input-icon" />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword((p) => !p)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        <Icon icon={showPassword ? EyeOff : Eye} size={18} />
                      </button>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={basicUserInfo.password}
                        onChange={handleBasicInfoChange}
                        placeholder="**************"
                        className="add-user-field-input-with-icon with-toggle"
                        required
                      />
                    </div>
                  </div>

                  <div className="add-user-form-field">
                    <label className="add-user-field-label">Confirm Password</label>
                    <div className="add-user-input-wrapper">
                      <Icon icon={Lock} size={18} className="add-user-input-icon" />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowConfirmPassword((p) => !p)}
                        aria-label={
                          showConfirmPassword
                            ? 'Hide confirm password'
                            : 'Show confirm password'
                        }
                      >
                        <Icon icon={showConfirmPassword ? EyeOff : Eye} size={18} />
                      </button>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={basicUserInfo.confirmPassword}
                        onChange={handleBasicInfoChange}
                        placeholder="**************"
                        className="add-user-field-input-with-icon with-toggle"
                        required
                      />
                    </div>
                  </div>

                  <div className="add-user-admin-button-container">
                    <button
                      type="button"
                      className="add-user-admin-btn"
                      onClick={handleNextFromStep1}
                      disabled={isAddUserSubmitting || (basicUserInfo.role === 'admin' && !adminAccessLevel)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleFinalAddUser}>
                <div className="add-user-modal-body">
                  <div className="add-user-form-field">
                    <label className="add-user-field-label">Region</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.regionCode}
                        onChange={(e) => {
                          const regionCode = e.target.value;
                          setPsgcSelection({ regionCode, provinceCode: '', municipalityCode: '', barangayCode: '' });
                          setPsgc((p) => ({ ...p, provinces: [], municipalities: [], barangays: [] }));
                          if (modalError) setModalError('');
                        }}
                        className="add-user-field-select"
                        disabled={psgcLoading.regions}
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.regions ? 'Loading...' : 'Select region'}
                        </option>
                        {psgc.regions.map((r) => (
                          <option key={r.code} value={r.code}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>

                  <div className="add-user-form-field">
                    <label className="add-user-field-label">Province</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.provinceCode}
                        onChange={(e) => {
                          const provinceCode = e.target.value;
                          setPsgcSelection((prev) => ({ ...prev, provinceCode, municipalityCode: '', barangayCode: '' }));
                          setPsgc((p) => ({ ...p, municipalities: [], barangays: [] }));
                          if (modalError) setModalError('');
                        }}
                        className="add-user-field-select"
                        disabled={!psgcSelection.regionCode || psgcLoading.provinces}
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.provinces ? 'Loading...' : 'Select province'}
                        </option>
                        {psgc.provinces.map((p) => (
                          <option key={p.code} value={p.code}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>

                  <div className="add-user-form-field">
                    <label className="add-user-field-label">Municipality</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.municipalityCode}
                        onChange={(e) => {
                          const municipalityCode = e.target.value;
                          setPsgcSelection((prev) => ({ ...prev, municipalityCode, barangayCode: '' }));
                          setPsgc((p) => ({ ...p, barangays: [] }));
                          if (modalError) setModalError('');
                        }}
                        className="add-user-field-select"
                        disabled={!psgcSelection.provinceCode || psgcLoading.municipalities}
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.municipalities ? 'Loading...' : 'Select municipality'}
                        </option>
                        {psgc.municipalities.map((m) => (
                          <option key={m.code} value={m.code}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>

                  <div className="add-user-form-field">
                    <label className="add-user-field-label">Barangay</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={psgcSelection.barangayCode}
                        onChange={(e) => {
                          setPsgcSelection((prev) => ({ ...prev, barangayCode: e.target.value }));
                          if (modalError) setModalError('');
                        }}
                        className="add-user-field-select"
                        disabled={!psgcSelection.municipalityCode || psgcLoading.barangays}
                        required
                      >
                        <option value="" disabled>
                          {psgcLoading.barangays ? 'Loading...' : 'Select barangay'}
                        </option>
                        {psgc.barangays.map((b) => (
                          <option key={b.code} value={b.code}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                      <span className="select-chevron">
                        <Icon icon={ChevronDown} size={16} />
                      </span>
                    </div>
                  </div>

                  <div className="add-user-admin-button-container">
                    <button
                      type="submit"
                      className="add-user-admin-btn"
                      disabled={isAddUserSubmitting}
                    >
                      {isAddUserSubmitting ? 'Adding...' : 'Add User'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <div className="add-user-modal-overlay" onClick={closeDeleteModal}>
          <div className="add-user-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="add-user-modal-close" onClick={closeDeleteModal}>
              ✕
            </button>

            <div className="add-user-modal-body">
              <h2 className="user-management-title" style={{ fontSize: '22px', marginBottom: '8px' }}>
                Remove Account Confirmation
              </h2>
              <p className="user-management-subtitle" style={{ marginTop: 0 }}>
                This will permanently remove <strong>{userToDelete?.name || userToDelete?.email || 'this user'}</strong> from the platform.
              </p>

              <div className="add-user-admin-button-container" style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  className="users-pagination-btn"
                  onClick={closeDeleteModal}
                  disabled={isDeleteSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="user-remove-btn"
                  onClick={confirmRemoveUser}
                  disabled={isDeleteSubmitting}
                >
                  {isDeleteSubmitting ? 'Removing...' : 'Confirm Removal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {isEditUserModalOpen && selectedUser && (
        <div className="add-user-modal-overlay" onClick={closeEditModal}>
          <div className="add-user-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="add-user-modal-close" onClick={closeEditModal}>
              ✕
            </button>

            <h2 className="user-management-title" style={{ fontSize: '22px', marginBottom: '8px' }}>
              Manage User Account
            </h2>
            <p className="user-management-subtitle" style={{ marginBottom: '20px' }}>
              Update profile details for <strong>{selectedUser.email}</strong>
            </p>

            {editModalError && <div className="add-user-error-message">{editModalError}</div>}

            <form onSubmit={handleEditSubmit}>
              <div className="add-user-modal-body">
                <div className="add-user-form-field">
                  <label className="add-user-field-label">Full Name</label>
                  <div className="add-user-input-wrapper">
                    <Icon icon={User} size={18} className="add-user-input-icon" />
                    <input
                      type="text"
                      name="name"
                      value={editFormData.name}
                      onChange={handleEditChange}
                      placeholder="Juan Dela Cruz"
                      className="add-user-field-input-with-icon"
                      required
                    />
                  </div>
                </div>

                <div className="add-user-form-field">
                  <label className="add-user-field-label">Username</label>
                  <div className="add-user-input-wrapper">
                    <Icon icon={User} size={18} className="add-user-input-icon" />
                    <input
                      type="text"
                      name="username"
                      value={editFormData.username}
                      onChange={handleEditChange}
                      placeholder="juan_dela_cruz"
                      className="add-user-field-input-with-icon"
                      required
                    />
                  </div>
                </div>

                <div className="add-user-form-field">
                  <label className="add-user-field-label">New Password (Leave blank to keep current)</label>
                  <div className="add-user-input-wrapper">
                    <Icon icon={Lock} size={18} className="add-user-input-icon" />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowEditPassword((p) => !p)}
                    >
                      <Icon icon={showEditPassword ? EyeOff : Eye} size={18} />
                    </button>
                    <input
                      type={showEditPassword ? 'text' : 'password'}
                      name="password"
                      value={editFormData.password}
                      onChange={handleEditChange}
                      placeholder="**************"
                      className="add-user-field-input-with-icon with-toggle"
                    />
                  </div>
                </div>

                <div className="add-user-form-field">
                  <label className="add-user-field-label">Confirm New Password</label>
                  <div className="add-user-input-wrapper">
                    <Icon icon={Lock} size={18} className="add-user-input-icon" />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowEditConfirmPassword((p) => !p)}
                    >
                      <Icon icon={showEditConfirmPassword ? EyeOff : Eye} size={18} />
                    </button>
                    <input
                      type={showEditConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={editFormData.confirmPassword}
                      onChange={handleEditChange}
                      placeholder="**************"
                      className="add-user-field-input-with-icon with-toggle"
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
                  <button
                    type="submit"
                    className="add-user-admin-btn"
                    disabled={isEditSubmitting}
                  >
                    {isEditSubmitting ? 'Saving Changes...' : 'Save Profile Changes'}
                  </button>


                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
